import { useState, useEffect, useCallback, useMemo } from "react";
import { usePageLoadMetric } from "../hooks/usePageLoadMetric";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { StoreSelectorButton } from "../components/molecules/StoreSelectorButton";
import { TabBar } from "../components/molecules/TabBar";
import { ActionIcon } from "../components/atoms/Icon";
import {
    ColumnFilterPopup,
    useColumnFilter,
    type SortDirection,
} from "../components/molecules/ColumnFilterPopup";
import { useMenuTitle } from "../shared/menu/useMenuTitle";
import { fetchShiftPatterns, ShiftPatternResponse, ShiftPatternTagInfo } from "../api/shiftPatterns";
import {
    CommonTable,
    CommonTableHeaderCell,
    CommonTableEditHeaderCell,
    CommonTableRow,
    CommonTableCell,
    CommonTableEditCell,
} from "../components/table";
import { Tag } from "../components/atoms/Tag/Tag";
import { resolveHex } from "../styles/colors";

/**
 * シフトパターンデータの型定義（表示用）
 */
interface ShiftPatternDisplay {
    id: number;
    pattern_id: string;
    pattern_name: string;
    display_name: string;
    work_start_time: string;
    work_end_time: string;
    break_time_1: number | null;
    tags: ShiftPatternTagInfo[];
    memo: string | null;
}

/** カラム総数（loading/empty行の colSpan 用） */
const SHIFT_PATTERN_COLUMN_COUNT = 9;
const EMPTY_VALUE_KEY = "__empty__";
const EMPTY_VALUE_LABEL = "(未設定)";
const TAG_SEPARATOR = ", ";

type SortColumn =
    | "id"
    | "name"
    | "short"
    | "start"
    | "end"
    | "break"
    | "tags"
    | "memo";

const toKey = (val: string | null | undefined): string =>
    val === null || val === undefined || val === "" ? EMPTY_VALUE_KEY : val;

const labelOrEmpty = (key: string): string =>
    key === EMPTY_VALUE_KEY ? EMPTY_VALUE_LABEL : key;

const tagsToKey = (tags: ShiftPatternTagInfo[]): string => {
    if (tags.length === 0) return EMPTY_VALUE_KEY;
    return tags.map((t) => t.name ?? "").join(TAG_SEPARATOR);
};

const breakToKey = (minutes: number | null): string =>
    minutes === null ? EMPTY_VALUE_KEY : String(minutes);

const breakLabel = (key: string): string =>
    key === EMPTY_VALUE_KEY ? EMPTY_VALUE_LABEL : `${key}分`;

/**
 * APIレスポンスを表示用に変換
 */
const convertToDisplayFormat = (data: ShiftPatternResponse): ShiftPatternDisplay => {
    return {
        id: data.id,
        pattern_id: data.code,
        pattern_name: data.name,
        display_name: data.short_name,
        work_start_time: data.work_start_time ?? "",
        work_end_time: data.work_end_time ?? "",
        break_time_1: data.break_time_minutes,
        tags: data.tags,
        memo: data.description,
    };
};

/**
 * シフトパターンページ
 */
const ShiftPatternsPage = () => {
    const menuTitle = useMenuTitle();
    const navigate = useNavigate();
    const [shiftPatterns, setShiftPatterns] = useState<ShiftPatternDisplay[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    usePageLoadMetric("shift-patterns", !loading);

    // データ取得
    const loadShiftPatterns = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchShiftPatterns({ page_size: 100 });
            const displayData = response.items.map(convertToDisplayFormat);
            setShiftPatterns(displayData);
        } catch (err) {
            console.error("Failed to fetch shift patterns:", err);
            setError(err instanceof Error ? err.message : "データの取得に失敗しました");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadShiftPatterns();
    }, [loadShiftPatterns]);

    // 新規登録ボタンクリック
    const handleNewPattern = () => {
        navigate("/shift-patterns/new");
    };

    // 編集ボタンクリック
    const handleEdit = (pattern: ShiftPatternDisplay) => {
        navigate(`/shift-patterns/${pattern.id}/edit`);
    };

    // 値の表示（空の場合は「-」）
    const displayValue = (value: string | number | null | undefined): string => {
        if (value === null || value === undefined || value === "") {
            return "-";
        }
        return String(value);
    };

    // 休憩時間の表示（分）
    const formatBreakTime = (minutes: number | null): string => {
        if (minutes === null) return "-";
        return `${minutes}分`;
    };

    // 列ごとの並び替え/絞り込み state
    const idFilter = useColumnFilter(shiftPatterns, (p) => toKey(p.pattern_id), labelOrEmpty);
    const nameFilter = useColumnFilter(shiftPatterns, (p) => toKey(p.pattern_name), labelOrEmpty);
    const shortFilter = useColumnFilter(shiftPatterns, (p) => toKey(p.display_name), labelOrEmpty);
    const startFilter = useColumnFilter(shiftPatterns, (p) => toKey(p.work_start_time), labelOrEmpty);
    const endFilter = useColumnFilter(shiftPatterns, (p) => toKey(p.work_end_time), labelOrEmpty);
    const breakFilter = useColumnFilter(shiftPatterns, (p) => breakToKey(p.break_time_1), breakLabel);
    const tagsFilter = useColumnFilter(shiftPatterns, (p) => tagsToKey(p.tags), labelOrEmpty);
    const memoFilter = useColumnFilter(shiftPatterns, (p) => toKey(p.memo), labelOrEmpty);

    // 並び替えは「同時に1列のみ」アクティブ
    const [activeSort, setActiveSort] = useState<{
        column: SortColumn;
        direction: "asc" | "desc";
    } | null>(null);

    const sortFor = (column: SortColumn): SortDirection =>
        activeSort?.column === column ? activeSort.direction : null;

    const handleSortChange = useCallback(
        (column: SortColumn) => (dir: SortDirection) => {
            setActiveSort(dir === null ? null : { column, direction: dir });
        },
        [],
    );

    // 表示用にソート/絞り込みを適用
    const displayedPatterns = useMemo(() => {
        let list = shiftPatterns;

        if (idFilter.isFiltering) {
            const set = new Set(idFilter.selected);
            list = list.filter((p) => set.has(toKey(p.pattern_id)));
        }
        if (nameFilter.isFiltering) {
            const set = new Set(nameFilter.selected);
            list = list.filter((p) => set.has(toKey(p.pattern_name)));
        }
        if (shortFilter.isFiltering) {
            const set = new Set(shortFilter.selected);
            list = list.filter((p) => set.has(toKey(p.display_name)));
        }
        if (startFilter.isFiltering) {
            const set = new Set(startFilter.selected);
            list = list.filter((p) => set.has(toKey(p.work_start_time)));
        }
        if (endFilter.isFiltering) {
            const set = new Set(endFilter.selected);
            list = list.filter((p) => set.has(toKey(p.work_end_time)));
        }
        if (breakFilter.isFiltering) {
            const set = new Set(breakFilter.selected);
            list = list.filter((p) => set.has(breakToKey(p.break_time_1)));
        }
        if (tagsFilter.isFiltering) {
            const set = new Set(tagsFilter.selected);
            list = list.filter((p) => set.has(tagsToKey(p.tags)));
        }
        if (memoFilter.isFiltering) {
            const set = new Set(memoFilter.selected);
            list = list.filter((p) => set.has(toKey(p.memo)));
        }

        if (activeSort) {
            const cmp = (a: ShiftPatternDisplay, b: ShiftPatternDisplay): number => {
                switch (activeSort.column) {
                    case "id":
                        return (a.pattern_id ?? "").localeCompare(b.pattern_id ?? "", "ja");
                    case "name":
                        return (a.pattern_name ?? "").localeCompare(b.pattern_name ?? "", "ja");
                    case "short":
                        return (a.display_name ?? "").localeCompare(b.display_name ?? "", "ja");
                    case "start":
                        return (a.work_start_time ?? "").localeCompare(b.work_start_time ?? "", "ja");
                    case "end":
                        return (a.work_end_time ?? "").localeCompare(b.work_end_time ?? "", "ja");
                    case "break": {
                        // null は最後に固定（asc/desc どちらでも末尾）
                        const av = a.break_time_1;
                        const bv = b.break_time_1;
                        if (av === null && bv === null) return 0;
                        if (av === null) return 1;
                        if (bv === null) return -1;
                        return av - bv;
                    }
                    case "tags":
                        return tagsToKey(a.tags).localeCompare(tagsToKey(b.tags), "ja");
                    case "memo":
                        return (a.memo ?? "").localeCompare(b.memo ?? "", "ja");
                }
            };
            list = [...list].sort((a, b) =>
                activeSort.direction === "asc" ? cmp(a, b) : -cmp(a, b),
            );
        }

        return list;
    }, [
        shiftPatterns,
        idFilter.isFiltering, idFilter.selected,
        nameFilter.isFiltering, nameFilter.selected,
        shortFilter.isFiltering, shortFilter.selected,
        startFilter.isFiltering, startFilter.selected,
        endFilter.isFiltering, endFilter.selected,
        breakFilter.isFiltering, breakFilter.selected,
        tagsFilter.isFiltering, tagsFilter.selected,
        memoFilter.isFiltering, memoFilter.selected,
        activeSort,
    ]);

    // ヘッダークリック時、ポップアップは「同時に1つだけ」開く
    const openOnly = useCallback(
        (which: SortColumn, anchor: HTMLButtonElement) => {
            const map = {
                id: idFilter,
                name: nameFilter,
                short: shortFilter,
                start: startFilter,
                end: endFilter,
                break: breakFilter,
                tags: tagsFilter,
                memo: memoFilter,
            } as const;
            const target = map[which];
            target.anchorRef.current = anchor;
            const willOpen = !target.open;
            target.setOpen(willOpen);
            if (willOpen) {
                Object.entries(map).forEach(([col, f]) => {
                    if (col !== which) f.setOpen(false);
                });
            }
        },
        [idFilter, nameFilter, shortFilter, startFilter, endFilter, breakFilter, tagsFilter, memoFilter],
    );

    return (
            <div className="flex-1 flex flex-col overflow-hidden">
                <PageHeader title={menuTitle} rightContent={<StoreSelectorButton />} />
                <TabBar />

                {/* アクションボタン */}
                <div className="px-6 pb-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={handleNewPattern}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-white text-base font-medium hover:bg-brand-600 transition-colors"
                        >
                            <ActionIcon name="add" className="w-4 h-4" />
                            新規登録
                        </button>
                        <button
                            type="button"
                            disabled
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed"
                        >
                            <ActionIcon name="edit" className="w-4 h-4" />
                            一括編集
                        </button>
                        <button
                            type="button"
                            disabled
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed"
                        >
                            <ActionIcon name="upload" className="w-4 h-4" />
                            アップロード
                        </button>
                        <button
                            type="button"
                            disabled
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed"
                        >
                            <ActionIcon name="download" className="w-4 h-4" />
                            ダウンロード
                        </button>
                    </div>
                </div>

                {/* エラー表示 */}
                {error && (
                    <div className="px-6 pb-4">
                        <div className="text-sm text-red-600">{error}</div>
                    </div>
                )}

                {/* テーブル */}
                <div className="flex-1 min-h-0 px-6 pb-6">
                    <CommonTable>
                        <thead className="sticky top-0 z-20">
                            <tr>
                                <CommonTableEditHeaderCell className="sticky left-0 z-30" />
                                <CommonTableHeaderCell
                                    sortable
                                    sortActive={idFilter.open}
                                    sortDirection={sortFor("id")}
                                    filterCount={idFilter.filterCount}
                                    onSort={(anchor) => openOnly("id", anchor)}
                                    className="min-w-[160px]"
                                >
                                    ID
                                </CommonTableHeaderCell>
                                <CommonTableHeaderCell
                                    sortable
                                    sortActive={nameFilter.open}
                                    sortDirection={sortFor("name")}
                                    filterCount={nameFilter.filterCount}
                                    onSort={(anchor) => openOnly("name", anchor)}
                                    className="min-w-[140px]"
                                >
                                    名称
                                </CommonTableHeaderCell>
                                <CommonTableHeaderCell
                                    sortable
                                    sortActive={shortFilter.open}
                                    sortDirection={sortFor("short")}
                                    filterCount={shortFilter.filterCount}
                                    onSort={(anchor) => openOnly("short", anchor)}
                                    className="min-w-[96px]"
                                >
                                    略称
                                </CommonTableHeaderCell>
                                <CommonTableHeaderCell
                                    sortable
                                    sortActive={startFilter.open}
                                    sortDirection={sortFor("start")}
                                    filterCount={startFilter.filterCount}
                                    onSort={(anchor) => openOnly("start", anchor)}
                                    className="min-w-[112px]"
                                >
                                    勤務開始時刻
                                </CommonTableHeaderCell>
                                <CommonTableHeaderCell
                                    sortable
                                    sortActive={endFilter.open}
                                    sortDirection={sortFor("end")}
                                    filterCount={endFilter.filterCount}
                                    onSort={(anchor) => openOnly("end", anchor)}
                                    className="min-w-[112px]"
                                >
                                    勤務終了時刻
                                </CommonTableHeaderCell>
                                <CommonTableHeaderCell
                                    sortable
                                    sortActive={breakFilter.open}
                                    sortDirection={sortFor("break")}
                                    filterCount={breakFilter.filterCount}
                                    onSort={(anchor) => openOnly("break", anchor)}
                                    className="min-w-[128px]"
                                >
                                    休憩分数
                                </CommonTableHeaderCell>
                                <CommonTableHeaderCell
                                    sortable
                                    sortActive={tagsFilter.open}
                                    sortDirection={sortFor("tags")}
                                    filterCount={tagsFilter.filterCount}
                                    onSort={(anchor) => openOnly("tags", anchor)}
                                    className="min-w-[160px]"
                                >
                                    タグ
                                </CommonTableHeaderCell>
                                <CommonTableHeaderCell
                                    sortable
                                    sortActive={memoFilter.open}
                                    sortDirection={sortFor("memo")}
                                    filterCount={memoFilter.filterCount}
                                    onSort={(anchor) => openOnly("memo", anchor)}
                                    className="min-w-[240px]"
                                >
                                    備考
                                </CommonTableHeaderCell>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <CommonTableRow>
                                    <CommonTableCell colSpan={SHIFT_PATTERN_COLUMN_COUNT} className="py-8 text-gray-500">
                                        読み込み中...
                                    </CommonTableCell>
                                </CommonTableRow>
                            ) : displayedPatterns.length === 0 ? (
                                <CommonTableRow>
                                    <CommonTableCell colSpan={SHIFT_PATTERN_COLUMN_COUNT} className="py-8 text-gray-500">
                                        {shiftPatterns.length === 0
                                            ? "シフトパターンデータがありません"
                                            : "該当するシフトパターンがありません"}
                                    </CommonTableCell>
                                </CommonTableRow>
                            ) : (
                                displayedPatterns.map((pattern) => (
                                    <CommonTableRow key={pattern.id}>
                                        <CommonTableEditCell
                                            className="sticky left-0 z-10 bg-[#E9F1F2]"
                                            onClick={() => handleEdit(pattern)}
                                        />
                                        <CommonTableCell>{displayValue(pattern.pattern_id)}</CommonTableCell>
                                        <CommonTableCell>{displayValue(pattern.pattern_name)}</CommonTableCell>
                                        <CommonTableCell>{displayValue(pattern.display_name)}</CommonTableCell>
                                        <CommonTableCell>{displayValue(pattern.work_start_time)}</CommonTableCell>
                                        <CommonTableCell>{displayValue(pattern.work_end_time)}</CommonTableCell>
                                        <CommonTableCell>{formatBreakTime(pattern.break_time_1)}</CommonTableCell>
                                        <CommonTableCell>
                                            <div className="flex flex-wrap justify-center gap-1">
                                                {pattern.tags.length > 0 ? (
                                                    pattern.tags.map((tag) => (
                                                        <Tag
                                                            key={tag.id}
                                                            label={tag.name ?? ""}
                                                            color={resolveHex(tag.color ?? "")}
                                                        />
                                                    ))
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </div>
                                        </CommonTableCell>
                                        <CommonTableCell className="!text-left">{displayValue(pattern.memo)}</CommonTableCell>
                                    </CommonTableRow>
                                ))
                            )}
                        </tbody>
                    </CommonTable>
                </div>

                {/* 列ごとの並び替え＋絞り込みポップアップ */}
                <ColumnFilterPopup
                    isOpen={idFilter.open}
                    onClose={() => idFilter.setOpen(false)}
                    anchorRef={idFilter.anchorRef}
                    sortDirection={sortFor("id")}
                    onSortChange={handleSortChange("id")}
                    options={idFilter.options}
                    selectedValues={idFilter.selected}
                    onSelectedValuesChange={idFilter.setSelected}
                />
                <ColumnFilterPopup
                    isOpen={nameFilter.open}
                    onClose={() => nameFilter.setOpen(false)}
                    anchorRef={nameFilter.anchorRef}
                    sortDirection={sortFor("name")}
                    onSortChange={handleSortChange("name")}
                    options={nameFilter.options}
                    selectedValues={nameFilter.selected}
                    onSelectedValuesChange={nameFilter.setSelected}
                />
                <ColumnFilterPopup
                    isOpen={shortFilter.open}
                    onClose={() => shortFilter.setOpen(false)}
                    anchorRef={shortFilter.anchorRef}
                    sortDirection={sortFor("short")}
                    onSortChange={handleSortChange("short")}
                    options={shortFilter.options}
                    selectedValues={shortFilter.selected}
                    onSelectedValuesChange={shortFilter.setSelected}
                />
                <ColumnFilterPopup
                    isOpen={startFilter.open}
                    onClose={() => startFilter.setOpen(false)}
                    anchorRef={startFilter.anchorRef}
                    sortDirection={sortFor("start")}
                    onSortChange={handleSortChange("start")}
                    options={startFilter.options}
                    selectedValues={startFilter.selected}
                    onSelectedValuesChange={startFilter.setSelected}
                />
                <ColumnFilterPopup
                    isOpen={endFilter.open}
                    onClose={() => endFilter.setOpen(false)}
                    anchorRef={endFilter.anchorRef}
                    sortDirection={sortFor("end")}
                    onSortChange={handleSortChange("end")}
                    options={endFilter.options}
                    selectedValues={endFilter.selected}
                    onSelectedValuesChange={endFilter.setSelected}
                />
                <ColumnFilterPopup
                    isOpen={breakFilter.open}
                    onClose={() => breakFilter.setOpen(false)}
                    anchorRef={breakFilter.anchorRef}
                    sortDirection={sortFor("break")}
                    onSortChange={handleSortChange("break")}
                    options={breakFilter.options}
                    selectedValues={breakFilter.selected}
                    onSelectedValuesChange={breakFilter.setSelected}
                />
                <ColumnFilterPopup
                    isOpen={tagsFilter.open}
                    onClose={() => tagsFilter.setOpen(false)}
                    anchorRef={tagsFilter.anchorRef}
                    sortDirection={sortFor("tags")}
                    onSortChange={handleSortChange("tags")}
                    options={tagsFilter.options}
                    selectedValues={tagsFilter.selected}
                    onSelectedValuesChange={tagsFilter.setSelected}
                />
                <ColumnFilterPopup
                    isOpen={memoFilter.open}
                    onClose={() => memoFilter.setOpen(false)}
                    anchorRef={memoFilter.anchorRef}
                    sortDirection={sortFor("memo")}
                    onSortChange={handleSortChange("memo")}
                    options={memoFilter.options}
                    selectedValues={memoFilter.selected}
                    onSelectedValuesChange={memoFilter.setSelected}
                />
            </div>
    );
};


export default ShiftPatternsPage;
